import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAppData } from '../store';
import * as db from '../lib/db';
import {
  RECURRENCE_LABELS,
  getSmartDefault,
  type RecurrenceType,
} from '../types';

const REMINDER_PRESETS = [60, 30, 14, 7, 3, 1, 0];

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

export default function ObligationForm() {
  const { obligationId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { properties, obligations, refresh } = useAppData();

  const editing = obligationId ? obligations.find((o) => o.id === obligationId) : undefined;
  const presetPropertyId = (location.state as { propertyId?: string } | null)?.propertyId ?? null;

  const [name, setName] = useState(editing?.name ?? '');
  const [propertyId, setPropertyId] = useState<string | ''>(
    editing?.property_id ?? presetPropertyId ?? ''
  );
  const [description, setDescription] = useState(editing?.description ?? '');
  const [dueDate, setDueDate] = useState(editing?.due_date ?? todayISO());
  const [recurrenceType, setRecurrenceType] = useState<RecurrenceType>(
    editing?.recurrence_type ?? 'annually'
  );
  const [recurrenceInterval, setRecurrenceInterval] = useState<number>(
    editing?.recurrence_interval ?? 2
  );
  const [amount, setAmount] = useState(editing?.amount?.toString() ?? '');
  const [payee, setPayee] = useState(editing?.payee ?? '');
  const [website, setWebsite] = useState(editing?.website ?? '');
  const [accountRef, setAccountRef] = useState(editing?.account_reference ?? '');
  const [autoPay, setAutoPay] = useState(editing?.auto_pay ?? false);
  const [notes, setNotes] = useState(editing?.notes ?? '');
  const [reminders, setReminders] = useState<number[]>([30, 7, 1]);
  const [appledSmartDefault, setAppliedSmartDefault] = useState(!!editing);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (editing) {
      db.listReminders(editing.id).then((r) => setReminders(r.map((x) => x.offset_days)));
    }
  }, [editing]);

  function handleNameBlur() {
    if (appledSmartDefault || editing) return;
    const suggestion = getSmartDefault(name);
    if (suggestion) {
      setRecurrenceType(suggestion.recurrence);
      setReminders(suggestion.reminders);
      setAppliedSmartDefault(true);
    }
  }

  function toggleReminder(offset: number) {
    setReminders((prev) =>
      prev.includes(offset) ? prev.filter((r) => r !== offset) : [...prev, offset].sort((a, b) => b - a)
    );
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim() || !dueDate) return;
    setSaving(true);
    setError(null);
    try {
      const payload = {
        name: name.trim(),
        property_id: propertyId || null,
        description: description.trim() || null,
        due_date: dueDate,
        recurrence_type: recurrenceType,
        recurrence_interval: ['every_x_months', 'every_x_years'].includes(recurrenceType)
          ? recurrenceInterval
          : null,
        amount: amount ? Number(amount) : null,
        payee: payee.trim() || null,
        website: website.trim() || null,
        account_reference: accountRef.trim() || null,
        auto_pay: autoPay,
        notes: notes.trim() || null,
      };

      if (editing) {
        await db.updateObligation(editing.id, payload);
        await db.setReminders(editing.id, reminders);
        await refresh();
        navigate(`/obligations/${editing.id}`);
      } else {
        const created = await db.createObligation(payload, reminders);
        await refresh();
        navigate(`/obligations/${created.id}`);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <form className="form card" onSubmit={handleSubmit}>
      <h1>{editing ? 'Edit obligation' : 'Add an obligation'}</h1>

      <label>
        Name
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          onBlur={handleNameBlur}
          placeholder="Insurance, Registration, Harbor master fee…"
          required
          autoFocus
        />
      </label>

      <label>
        Property
        <select value={propertyId} onChange={(e) => setPropertyId(e.target.value)}>
          <option value="">No specific property</option>
          {properties.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </select>
      </label>

      <label>
        Description (optional)
        <input value={description} onChange={(e) => setDescription(e.target.value)} />
      </label>

      <label>
        Due date
        <input
          type="date"
          value={dueDate}
          onChange={(e) => setDueDate(e.target.value)}
          required
        />
      </label>

      <label>
        Repeats
        <select
          value={recurrenceType}
          onChange={(e) => setRecurrenceType(e.target.value as RecurrenceType)}
        >
          {Object.entries(RECURRENCE_LABELS).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
      </label>

      {(recurrenceType === 'every_x_months' || recurrenceType === 'every_x_years') && (
        <label>
          Every
          <input
            type="number"
            min={1}
            value={recurrenceInterval}
            onChange={(e) => setRecurrenceInterval(Number(e.target.value))}
          />
        </label>
      )}

      <fieldset className="reminders-field">
        <legend>Remind me before</legend>
        <div className="reminder-chips">
          {REMINDER_PRESETS.map((offset) => (
            <button
              type="button"
              key={offset}
              className={`chip ${reminders.includes(offset) ? 'chip-selected' : ''}`}
              onClick={() => toggleReminder(offset)}
            >
              {offset === 0 ? 'Day of' : `${offset} days`}
            </button>
          ))}
        </div>
      </fieldset>

      <label>
        Amount (optional)
        <input
          type="number"
          step="0.01"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
        />
      </label>

      <label>
        Payee / vendor (optional)
        <input value={payee} onChange={(e) => setPayee(e.target.value)} />
      </label>

      <label>
        Website (optional)
        <input
          type="url"
          value={website}
          onChange={(e) => setWebsite(e.target.value)}
          placeholder="https://…"
        />
      </label>

      <label>
        Account / reference number (optional)
        <input value={accountRef} onChange={(e) => setAccountRef(e.target.value)} />
      </label>

      <label className="checkbox-label">
        <input type="checkbox" checked={autoPay} onChange={(e) => setAutoPay(e.target.checked)} />
        Auto-pay
      </label>

      <label>
        Notes
        <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} />
      </label>

      {error && <p className="error">{error}</p>}

      <div className="form-actions">
        <button type="button" className="btn-secondary" onClick={() => navigate(-1)}>
          Cancel
        </button>
        <button type="submit" className="btn-primary" disabled={saving}>
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
    </form>
  );
}
